import { noop } from 'lodash/fp';
import { ComponentProps, useEffect, useState, VFC } from 'react';
import { components as ReactSelectComponents, InputActionMeta, StylesConfig } from 'react-select';

import SelectOption from 'dashboard/components/CaseDataManagerSchemaSelect/SelectOption';
import {
  getOptionForValue,
  getSchemaOptions,
  Option,
  validatePath,
} from 'dashboard/components/CaseDataManagerSchemaSelect/utils';
import ReactSelect from 'dashboard/components/ReactSelect';
import useToggle from 'lib/hooks/useToggle';
import { JsonSchema } from 'lib/json-schema';

const customStyles: StylesConfig = {
  control: () => ({
    height: 'auto',
  }),
  indicatorsContainer: () => ({
    display: 'none',
  }),
  multiValueLabel: () => ({
    padding: '1px',
  }),
  valueContainer: () => ({
    height: 'auto',
    minHeight: '30px',
    maxHeight: '80px',
    overflowY: 'auto',
    paddingTop: 0,
    paddingBottom: 0,
  }),
};

// NB: We need this to override isHidden so that the in-progress input text doesn't disappear.
const CustomInput = ({ ...props }: ComponentProps<typeof ReactSelectComponents.Input>) => {
  return <ReactSelectComponents.Input {...props} isHidden={false} />;
};

export interface CaseDataManagerSchemaSelectProps
  extends Partial<ComponentProps<typeof ReactSelect>> {
  jsonSchema: JsonSchema;
  inputValue: string;
  onValueChange: (value: string) => void;
  onFieldSelect?: (data: Option) => void;
}

const CaseDataManagerSchemaSelect: VFC<CaseDataManagerSchemaSelectProps> = ({
  jsonSchema,
  inputValue,
  onValueChange,
  onFieldSelect = noop,
  ...reactSelectProps
}: CaseDataManagerSchemaSelectProps) => {
  const [schemaOptions, setSchemaOptions] = useState<Option[]>(
    getSchemaOptions({ schema: jsonSchema }),
  );
  const [searchValue, setSearchValue] = useState('');
  const [isDropdownOpen, { enable: openDropdown, disable: closeDropdown }] = useToggle(false);

  const wrappedGetOptionForValue = (value: string) => getOptionForValue(value, jsonSchema);

  const getSchemaOptionsForPath = (path: string) => {
    const { schemaOptions } = validatePath(path, jsonSchema);
    const lastPathPortion = path.split('.').pop();
    return schemaOptions.filter(({ property }) => {
      return lastPathPortion && property.includes(lastPathPortion);
    });
  };

  const selectFinalField = (option: Option) => {
    onValueChange(option.value);
    onFieldSelect(option);
    setSchemaOptions([]);
    closeDropdown();
  };

  const handleOptionSelect = (option: Option): void => {
    const schemaOptions = getSchemaOptions(option);

    setSchemaOptions([...schemaOptions, option]);
    setSearchValue(option.value);

    // auto select if only 1 option
    if (schemaOptions.length === 1) {
      return handleOptionSelect(schemaOptions[0]);
    }

    if (schemaOptions.length > 1) {
      return setSearchValue(`${option.value}.`);
    }

    if (schemaOptions.length === 0) {
      selectFinalField(option);
    }
  };

  const handleInputChange = (inputValue: string, e: InputActionMeta) => {
    if (e.action !== 'input-change') {
      return;
    }
    if (inputValue === '') {
      onValueChange('');
    }

    const newOptions = getSchemaOptionsForPath(inputValue);
    setSchemaOptions(newOptions);
    setSearchValue(inputValue);
    openDropdown();
  };

  const handleChange = (val: string) => {
    handleOptionSelect(wrappedGetOptionForValue(val));
  };

  const handleInputFocus = () => {
    openDropdown();
    setSearchValue(searchValue || (inputValue as string));
  };

  useEffect(() => {
    const newTopLevelOptions = getSchemaOptions({ schema: jsonSchema });
    setSchemaOptions(newTopLevelOptions);
    setSearchValue(inputValue);
  }, [jsonSchema, inputValue, setSchemaOptions]);

  return (
    <ReactSelect
      value={inputValue}
      inputValue={searchValue}
      options={schemaOptions}
      menuIsOpen={isDropdownOpen}
      styles={customStyles as any}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      components={{ Option: SelectOption, Input: CustomInput }}
      onInputChange={handleInputChange}
      onFocus={handleInputFocus}
      onBlur={closeDropdown}
      onChange={handleChange}
      portal
      {...reactSelectProps}
    />
  );
};

export default CaseDataManagerSchemaSelect;
